import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getApiUrl,
  getCountries,
  getRegions,
  getCastles,
  getSearchSuggestions,
  getCastle,
  getCountry,
  getFeaturedCastles,
  getFeaturedBlogPosts,
  getBlogPosts,
  getBlogPost,
  getBlogCategories,
  getBlogTags,
  getUserCastles,
} from './api';
import type { ContributionPayload } from '@/types';

// Query keys
export const queryKeys = {
  countries: ['countries'] as const,
  regions: (countryId: number) => ['regions', countryId] as const,
  castles: (params: Record<string, any>) => ['castles', params] as const,
  featuredCastles: ['featuredCastles'] as const,
  castle: (id: string) => ['castle', id] as const,
  country: (idOrSlug: string) => ['country', idOrSlug] as const,
  searchSuggestions: (query: string) => ['searchSuggestions', query] as const,
  featuredBlogPosts: (limit?: number) => ['featuredBlogPosts', limit] as const,
  blogPosts: (params: Record<string, any>) => ['blogPosts', params] as const,
  blogPost: (slug: string, id: number) => ['blogPost', slug, id] as const,
  blogCategories: ['blogCategories'] as const,
  blogTags: ['blogTags'] as const,
  weather: (lat: number, lon: number, units: string) => ['weather', lat, lon, units] as const,
  userCastles: (type: string, page?: number, limit?: number) => ['userCastles', type, page, limit] as const,
};

// Queries
export function useCountries() {
  return useQuery({
    queryKey: queryKeys.countries,
    queryFn: getCountries,
  });
}

export function useRegions(countryId: number) {
  return useQuery({
    queryKey: queryKeys.regions(countryId),
    queryFn: () => getRegions(countryId),
    enabled: !!countryId,
  });
}

export function useCastles(
  params: {
    country?: string;
    countryId?: number;
    region?: string;
    regionId?: number;
    city?: string;
    search?: string;
    period?: string;
    status?: string;
    featured?: boolean;
    limit?: number;
    page?: number;
  } = {}
) {
  return useQuery({
    queryKey: queryKeys.castles(params),
    queryFn: () => getCastles(params),
  });
}

export function useFeaturedCastles() {
  return useQuery({
    queryKey: queryKeys.featuredCastles,
    queryFn: getFeaturedCastles,
  });
}

export function useCastle(id: string) {
  return useQuery({
    queryKey: queryKeys.castle(id),
    queryFn: () => getCastle(id),
    enabled: !!id,
  });
}

export function useCountry(idOrSlug: string) {
  return useQuery({
    queryKey: queryKeys.country(idOrSlug),
    queryFn: () => getCountry(idOrSlug),
    enabled: !!idOrSlug,
  });
}

export function useSearchSuggestions(query: string) {
  return useQuery({
    queryKey: queryKeys.searchSuggestions(query),
    queryFn: () => getSearchSuggestions(query),
    enabled: query.length >= 2,
  });
}

export function useFeaturedBlogPosts(limit: number = 3) {
  return useQuery({
    queryKey: queryKeys.featuredBlogPosts(limit),
    queryFn: () => getFeaturedBlogPosts(limit),
  });
}

export function useBlogPosts(
  params: {
    page?: number;
    limit?: number;
    category?: string;
    tag?: string;
    search?: string;
  } = {}
) {
  return useQuery({
    queryKey: queryKeys.blogPosts(params),
    queryFn: () => getBlogPosts(params),
  });
}

export function useBlogPost(slug: string, id: number) {
  return useQuery({
    queryKey: queryKeys.blogPost(slug, id),
    queryFn: () => getBlogPost(slug, id),
    enabled: !!slug && !!id,
  });
}

export function useBlogCategories() {
  return useQuery({
    queryKey: queryKeys.blogCategories,
    queryFn: getBlogCategories,
  });
}

export function useBlogTags() {
  return useQuery({
    queryKey: queryKeys.blogTags,
    queryFn: getBlogTags,
  });
}

export function useUserCastles(type: 'favourite' | 'visited', page: number = 1, limit: number = 12) {
  return useQuery({
    queryKey: queryKeys.userCastles(type, page, limit),
    queryFn: () => getUserCastles({ type, page, limit }),
    // only run on client where token is available
    enabled: typeof window !== 'undefined',
  });
}

export function useWeather(lat: number, lon: number, units: 'metric' | 'imperial' = 'metric') {
  return useQuery({
    queryKey: queryKeys.weather(lat, lon, units),
    queryFn: async () => {
      const url = `/api/weather?lat=${encodeURIComponent(lat)}&lon=${encodeURIComponent(lon)}&units=${encodeURIComponent(units)}`;
      const res = await fetch(url);
      if (!res.ok) {
        let errText = 'Failed to fetch weather';
        try {
          const json = await res.json();
          if (json && typeof json.error === 'string') errText = json.error;
          else if (json && typeof json.message === 'string') errText = json.message;
        } catch (_e) {
          if (res.statusText) errText = res.statusText;
        }
        throw new Error(errText);
      }
      return res.json();
    },
    // Keep cached for a short while; React Query default staleTime is 0, but we'll keep 5 minutes
    staleTime: 1000 * 60 * 5,
  });
}

// Blog Comment mutations
export function useSubmitBlogComment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: {
      blogPostId: number;
      comment: string;
      authorName: string;
      authorEmail?: string;
      turnstileToken: string;
    }) => {
      const response = await fetch(getApiUrl('/blog-comments'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });
      if (!response.ok) {
        let errText = 'Failed to submit comment';
        try {
          const json = await response.json();
          if (json && typeof json.error === 'string') errText = json.error;
          else if (json && typeof json.message === 'string') errText = json.message;
        } catch (_e) {
          if (response.statusText) errText = response.statusText;
        }
        throw new Error(errText);
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['blogPost'] });
    },
  });
}

// Mutations for forms
export function useSubmitReview() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: {
      castleId: number;
      rating: number;
      comment: string;
      authorName: string;
      authorEmail?: string;
      turnstileToken: string;
    }) => {
      const response = await fetch(getApiUrl('/reviews'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });
      if (!response.ok) {
        let errText = 'Failed to submit review';
        try {
          const json = await response.json();
          if (json && typeof json.error === 'string') errText = json.error;
          else if (json && typeof json.message === 'string') errText = json.message;
        } catch (_e) {
          // ignore JSON parse errors and fall back to status text
          if (response.statusText) errText = response.statusText;
        }
        throw new Error(errText);
      }
      return response.json();
    },
    onSuccess: () => {
      // Invalidate related queries
      queryClient.invalidateQueries({ queryKey: ['castle'] });
    },
  });
}

export function useSubmitContribution() {
  return useMutation({
    mutationFn: async (data: ContributionPayload) => {
      const response = await fetch(getApiUrl('/contributions'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });
      if (!response.ok) {
        let errText = 'Failed to submit contribution';
        try {
          const json = await response.json();
          if (json && typeof json.error === 'string') errText = json.error;
          else if (json && typeof json.message === 'string') errText = json.message;
        } catch (_e) {
          // ignore JSON parse errors and fall back to status text
          if (response.statusText) errText = response.statusText;
        }
        throw new Error(errText);
      }
      return response.json();
    },
  });
}

export function useSubmitContact() {
  return useMutation({
    mutationFn: async (data: {
      email: string;
      title: string;
      message: string;
      turnstileToken: string;
    }) => {
      const response = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });
      if (!response.ok) {
        let errText = 'Failed to send message';
        try {
          const json = await response.json();
          if (json && typeof json.error === 'string') errText = json.error;
          else if (json && typeof json.message === 'string') errText = json.message;
        } catch (_e) {
          // ignore JSON parse errors and fall back to status text
          if (response.statusText) errText = response.statusText;
        }
        throw new Error(errText);
      }
      return response.json();
    },
  });
}
